import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config/environment';
import { getSpiritualIcon } from '../../utils/spiritualIcons';
import { subServicesData } from '../../data/servicesData';
import './ProviderDashboard.css';

// Types for API response
interface Location {
  label: string;
  line1: string;
  line2: string;
  city: string;
  state: string;
  postalCode: string;
  latitude: number | null;
  longitude: number | null;
}

interface BookingRequest {
  serviceRequestUuid: string;
  serviceId: number;
  customerName: string;
  slotStart: string;
  slotEnd: string;
  requestStatus: string;
  priceEstimate: number;
  location: Location;
  createdAt: string;
}

interface RecentBookingsResponse {
  providerUuid: string;
  newRequests: BookingRequest[];
  previousRequests: BookingRequest[];
  totalNewRequests: number;
  totalPreviousRequests: number;
}

// Enhanced Provider Services API Types
interface BookingStats {
  totalBookings: number;
  pendingBookings: number;
  completedBookings: number;
  thisMonthBookings: number;
  revenue: number;
}

interface ProviderService {
  id: number;
  providerId: number;
  serviceId: number;
  serviceName: string;
  experienceYears: number;
  customPrice: number;
  active: boolean;
  bookingStats: BookingStats;
}

interface DashboardSummary {
  totalActiveServices: number;
  totalBookings: number;
  thisMonthRevenue: number;
  averageRating: number;
}

interface ProviderServicesResponse {
  providerServices: ProviderService[];
  summary: DashboardSummary;
}

// Service Management Types
interface MasterService {
  id: number;
  name: string;
  description: string;
  basePrice: number;
}

interface ProviderServiceSelection {
  serviceId: number;
  isSelected: boolean;
  experienceYears?: number;
}

const ProviderDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // State for booking data
  const [bookingData, setBookingData] = useState<RecentBookingsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // State for enhanced provider services data
  const [providerServicesData, setProviderServicesData] = useState<ProviderServicesResponse | null>(null);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [servicesError, setServicesError] = useState<string | null>(null);

  // Service management state
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [selectedServices, setSelectedServices] = useState<ProviderServiceSelection[]>([]);
  const [masterServices, setMasterServices] = useState<MasterService[]>([]);

  // Separate useEffect for each API call to prevent unnecessary re-fetching
  
  // Fetch recent bookings when user changes
  useEffect(() => {
    let isMounted = true; // Cleanup flag
    
    const fetchRecentBookings = async () => {
      if (!user?.userUuid) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // Get access token for authentication
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('No access token found');
        }
        
        const response = await fetch(`${config.authServerUrl}/api/provider/${user.userUuid}/recent-bookings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data: RecentBookingsResponse = await response.json();
        
        // Only update state if component is still mounted
        if (isMounted) {
          setBookingData(data);
        }
      } catch (err) {
        if (isMounted) {
          setError(err instanceof Error ? err.message : 'Failed to load bookings');
          console.error('Error fetching recent bookings:', err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchRecentBookings();
    
    // Cleanup function
    return () => {
      isMounted = false;
    };
  }, [user?.userUuid]);

  // Fetch provider services when user changes
  useEffect(() => {
    let isMounted = true;
    
    const fetchProviderServices = async () => {
      if (!user?.userUuid) {
        setServicesLoading(false);
        return;
      }
      
      try {
        setServicesLoading(true);
        setServicesError(null);
        
        // Get access token for authentication
        const token = localStorage.getItem('access_token');
        if (!token) {
          throw new Error('No access token found');
        }
        
        const response = await fetch(`${config.authServerUrl}/api/provider-services/user/${user.userUuid}/dashboard?include=bookingStats`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });
        
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const data: ProviderServicesResponse = await response.json();
        
        if (isMounted) {
          setProviderServicesData(data);
          console.log('✅ Provider services data loaded:', data);
        }
      } catch (err) {
        if (isMounted) {
          setServicesError(err instanceof Error ? err.message : 'Failed to load provider services');
          console.error('❌ Error fetching provider services:', err);
        }
      } finally {
        if (isMounted) {
          setServicesLoading(false);
        }
      }
    };
    
    fetchProviderServices();
    
    return () => {
      isMounted = false;
    };
  }, [user?.userUuid]);

  // Fetch master services once on component mount
  useEffect(() => {
    let isMounted = true;
    
    const fetchMasterServices = async () => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) {
          console.warn('No access token found for master services');
          return;
        }

        const response = await fetch(`${config.authServerUrl}/api/services`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const services: MasterService[] = await response.json();
        
        if (isMounted) {
          setMasterServices(services);
          
          // Initialize selected services state
          setSelectedServices(services.map(service => ({
            serviceId: service.id,
            isSelected: false
          })));
        }

      } catch (err) {
        if (isMounted) {
          console.error('Error fetching master services:', err);
          setError('Failed to load services list');
        }
      }
    };

    fetchMasterServices();
    
    return () => {
      isMounted = false;
    };
  }, []); // Only run once on mount

  // Retry function for provider services
  const retryFetchProviderServices = async () => {
    if (!user?.userUuid) return;
    
    try {
      setServicesLoading(true);
      setServicesError(null);
      
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token found');
      }
      
      const response = await fetch(`${config.authServerUrl}/api/provider-services/user/${user.userUuid}/dashboard?include=bookingStats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: ProviderServicesResponse = await response.json();
      setProviderServicesData(data);
      console.log('✅ Provider services data loaded (retry):', data);
    } catch (err) {
      setServicesError(err instanceof Error ? err.message : 'Failed to load provider services');
      console.error('❌ Error fetching provider services (retry):', err);
    } finally {
      setServicesLoading(false);
    }
  };

  // Get full name or fallback to username
  const getFullName = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    } else if (user?.firstName) {
      return user.firstName;
    } else {
      return user?.username || 'Provider';
    }
  };

  // Get service name by ID
  const getServiceNameById = (serviceId: number): string => {
    const allServices = Object.values(subServicesData).flat();
    const service = allServices.find(s => s.id === serviceId);
    return service?.name || `Service ${serviceId}`;
  };

  // Helper functions
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const formatLocation = (location: Location) => {
    const parts = [location.city, location.state].filter(Boolean);
    return parts.join(', ');
  };

  const getStatusDisplay = (status: string) => {
    switch (status) {
      case 'PENDING_PROVIDER':
        return { text: 'Pending', class: 'pending' };
      case 'ASSIGNED':
        return { text: 'Confirmed', class: 'confirmed' };
      case 'COMPLETED':
        return { text: 'Completed', class: 'completed' };
      case 'CANCELLED':
        return { text: 'Cancelled', class: 'cancelled' };
      default:
        return { text: status, class: 'default' };
    }
  };

  const handleAcceptBooking = async (serviceRequestUuid: string) => {
    try {
      // Get access token for authentication
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error('No access token found');
        return;
      }

      if (!user?.userUuid) {
        console.error('No provider UUID found');
        return;
      }

      const response = await fetch(`${config.authServerUrl}/api/provider/${user.userUuid}/service-request/${serviceRequestUuid}/accept`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Booking accepted successfully:', result.message);
        
        // Refresh the booking data to show updated status
        const refreshResponse = await fetch(`${config.authServerUrl}/api/provider/${user.userUuid}/recent-bookings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });
        
        if (refreshResponse.ok) {
          const refreshedData: RecentBookingsResponse = await refreshResponse.json();
          setBookingData(refreshedData);
        }
      } else {
        console.error('Failed to accept booking:', result.message);
      }
    } catch (err) {
      console.error('Error accepting booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to accept booking');
    }
  };

  const handleRejectBooking = async (serviceRequestUuid: string) => {
    try {
      // Get access token for authentication
      const token = localStorage.getItem('access_token');
      if (!token) {
        console.error('No access token found');
        return;
      }

      if (!user?.userUuid) {
        console.error('No provider UUID found');
        return;
      }

      // TODO: Replace with actual reject endpoint when available
      console.log('Rejecting booking:', serviceRequestUuid);
      
      // Example implementation for when reject endpoint is ready:
      /*
      const response = await fetch(`${config.authServerUrl}/api/provider/${user.userUuid}/service-request/${serviceRequestUuid}/reject`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success) {
        console.log('✅ Booking rejected successfully:', result.message);
        
        // Refresh the booking data to show updated status
        const refreshResponse = await fetch(`${config.authServerUrl}/api/provider/${user.userUuid}/recent-bookings`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          credentials: 'include',
        });
        
        if (refreshResponse.ok) {
          const refreshedData: RecentBookingsResponse = await refreshResponse.json();
          setBookingData(refreshedData);
        }
      } else {
        console.error('Failed to reject booking:', result.message);
      }
      */
    } catch (err) {
      console.error('Error rejecting booking:', err);
      setError(err instanceof Error ? err.message : 'Failed to reject booking');
    }
  };

  const handleViewAllBookings = () => {
    navigate('/provider/bookings');
  };

  const handleManageAllServices = () => {
    navigate('/provider/services');
  };

  const handleUpdateProfile = () => {
    navigate('/provider/profile?edit=true');
  };

  const handleManageCalendar = () => {
    navigate('/provider/calendar');
  };

  // Service Management Functions
  const openAddServiceModal = () => {
    setShowServiceModal(true);
  };

  const handleServiceSelection = (serviceId: number, isSelected: boolean) => {
    setSelectedServices(prev => prev.map(service => 
      service.serviceId === serviceId 
        ? { ...service, isSelected }
        : service
    ));
  };

  const handleExperienceChange = (serviceId: number, years: number) => {
    setSelectedServices(prev => prev.map(service => 
      service.serviceId === serviceId 
        ? { ...service, experienceYears: years }
        : service
    ));
  };

  const handleSubmitServices = async () => {
    if (!user?.userUuid) return;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) throw new Error('No access token found');

      // Get only selected services
      const selectedServicesList = selectedServices.filter(service => service.isSelected);
      
      if (selectedServicesList.length === 0) {
        setError('Please select at least one service');
        return;
      }

      // Build the batch payload for all selected services
      const servicesPayload = selectedServicesList.map(service => {
        const masterService = masterServices.find(ms => ms.id === service.serviceId);
        return {
          serviceId: service.serviceId,
          experienceYears: service.experienceYears || 1, // Default to 1 year if not specified
          customPrice: masterService?.basePrice || 0 // Use service catalog's base price
        };
      });

      const batchPayload = {
        services: servicesPayload
      };

      // Submit all services in a single batch request
      const response = await fetch(`${config.authServerUrl}/api/provider-services/user/${user.userUuid}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(batchPayload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const results = await response.json();
      console.log('All services added successfully:', results);
      
      // Show success message (you can replace this with a proper toast/notification)
      alert(`Successfully added ${results.length} service(s)!`);
      
      setShowServiceModal(false);
      // Reset selections
      setSelectedServices(prev => prev.map(service => ({ 
        ...service, 
        isSelected: false, 
        experienceYears: undefined
      })));
      
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save services');
      console.error('Error saving services:', err);
    }
  };

  const getMasterServiceById = (serviceId: number): MasterService | undefined => {
    return masterServices.find(service => service.id === serviceId);
  };

  return (
    <div className="provider-dashboard">
      {/* Header Section */}
      <div className="dashboard-header">
        <div className="header-content">
          <div className="welcome-section">
      
            <p>Welcome back, {getFullName()} Ji!</p>
          </div>
          <div className="quick-actions-header">
            <button className="action-btn primary" onClick={openAddServiceModal}>Add New Service</button>
            {/* <button className="action-btn secondary" onClick={handleManageCalendar}>Manage Calendar</button> */}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-content">
            <h3>Active Services</h3>
            <div className="stat-number">
              {servicesLoading ? '...' : (providerServicesData?.summary.totalActiveServices || 0)}
            </div>
          </div>
          <div className="stat-icon">🛍️</div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>Total Bookings</h3>
            <div className="stat-number">
              {servicesLoading ? '...' : (providerServicesData?.summary.totalBookings || 0)}
            </div>
          </div>
          <div className="stat-icon">📅</div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>New Requests</h3>
            <div className="stat-number">{bookingData?.totalNewRequests || 0}</div>
          </div>
          <div className="stat-icon">⏳</div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>This Month's Revenue</h3>
            <div className="stat-number">
              ₹{servicesLoading ? '...' : (providerServicesData?.summary.thisMonthRevenue?.toLocaleString('en-IN') || 0)}
            </div>
          </div>
          <div className="stat-icon">💰</div>
        </div>
        <div className="stat-card">
          <div className="stat-content">
            <h3>Rating</h3>
            <div className="stat-number">
              {servicesLoading ? '...' : (providerServicesData?.summary.averageRating ? 
                providerServicesData.summary.averageRating.toFixed(1) : 'N/A')}
            </div>
          </div>
          <div className="stat-icon">⭐</div>
        </div>
      </div>

      {/* Main Content - Two Column Layout */}
      <div className="dashboard-main">
        {/* Left Column - Bookings */}
        <div className="bookings-section">
          <div className="section-header">
            <h2>Recent Bookings</h2>
            {/* <button className="view-all-btn" onClick={handleViewAllBookings}>View All</button> */}
          </div>
          
          <div className="bookings-list">
            {loading ? (
              <div className="loading-state">
                <p>Loading recent bookings...</p>
              </div>
            ) : error ? (
              <div className="error-state">
                <p>Error: {error}</p>
              </div>
            ) : (
              <>
                {/* New Requests (Pending) */}
                {bookingData?.newRequests?.map((booking) => (
                  <div key={booking.serviceRequestUuid} className="booking-card">
                    <div className="booking-header">
                      <h4>{getServiceNameById(booking.serviceId)}</h4>
                      {/* <div className="booking-amount">₹{booking.priceEstimate.toLocaleString('en-IN')}</div> */}
                    </div>
                    <div className="booking-details">
                      <p><strong>Customer:</strong> {booking.customerName}</p>
                      <p><strong>Date:</strong> {formatDate(booking.slotStart)}</p>
                      <p><strong>Time:</strong> {formatTime(booking.slotStart)}</p>
                      <p><strong>Location:</strong> {formatLocation(booking.location)}</p>
                    </div>
                    <div className="booking-actions">
                      <button 
                        className="accept-btn"
                        onClick={() => handleAcceptBooking(booking.serviceRequestUuid)}
                      >
                        Accept
                      </button>
                      <button 
                        className="reject-btn"
                        onClick={() => handleRejectBooking(booking.serviceRequestUuid)}
                      >
                        Reject
                      </button>
                    </div>
                  </div>
                ))}
                
                {/* Previous Requests (Already processed) */}
                {bookingData?.previousRequests?.slice(0, 2).map((booking) => {
                  const statusInfo = getStatusDisplay(booking.requestStatus);
                  return (
                    <div key={booking.serviceRequestUuid} className="booking-card">
                      <div className="booking-header">
                        <h4>{getServiceNameById(booking.serviceId)}</h4>
                        <div className="booking-amount">₹{booking.priceEstimate.toLocaleString('en-IN')}</div>
                      </div>
                      <div className="booking-details">
                        <p><strong>Customer:</strong> {booking.customerName}</p>
                        <p><strong>Date:</strong> {formatDate(booking.slotStart)}</p>
                        <p><strong>Time:</strong> {formatTime(booking.slotStart)}</p>
                        <p><strong>Location:</strong> {formatLocation(booking.location)}</p>
                      </div>
                      <div className={`booking-status ${statusInfo.class}`}>
                        <span>✓ {statusInfo.text}</span>
                      </div>
                    </div>
                  );
                })}
                
                {/* No bookings state */}
                {(!bookingData?.newRequests?.length && !bookingData?.previousRequests?.length) && (
                  <div className="no-bookings-state">
                    <p>No recent bookings found</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Right Column - Services & Actions */}
        <div className="services-section">
          <div className="section-header">
            <h2>My Services</h2>
            {/* <button className="view-all-btn" onClick={handleManageAllServices}>Manage All</button> */}
          </div>
          
          <div className="services-list">
            {servicesLoading ? (
              <div className="loading-services">
                <p>Loading services...</p>
              </div>
            ) : servicesError ? (
              <div className="error-services">
                <p>Error loading services: {servicesError}</p>
                <button onClick={retryFetchProviderServices} className="retry-btn">Retry</button>
              </div>
            ) : providerServicesData?.providerServices.length === 0 ? (
              <div className="no-services">
                <p>No services configured yet.</p>
                <button onClick={() => setShowServiceModal(true)} className="add-service-btn">Add Your First Service</button>
              </div>
            ) : (
              providerServicesData?.providerServices.map((service) => (
                <div key={service.id} className="service-item">
                  <div className="service-info">
                    <h4>{service.serviceName}</h4>
                    <p>₹{service.customPrice.toLocaleString('en-IN')}</p>
                  </div>
                  <div className="service-stats">
                    <span className="bookings-count">
                      {service.bookingStats.totalBookings} bookings
                    </span>
                    <span className={`service-status ${service.active ? 'active' : 'inactive'}`}>
                      {service.active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Additional Quick Actions */}
          <div className="additional-actions">
            <h3>Quick Actions</h3>
            <div className="action-grid">
              {/* <button className="quick-action-btn">
                <span className="action-icon">📊</span>
                View Analytics
              </button> */}
              <button className="quick-action-btn" onClick={handleUpdateProfile}>
                <span className="action-icon">👤</span>
                Update Profile
              </button>
              {/* <button className="quick-action-btn">
                <span className="action-icon">💬</span>
                Customer Reviews
              </button> */}
              {/* <button className="quick-action-btn">
                <span className="action-icon">💳</span>
                Payment History
              </button> */}
            </div>
          </div>
        </div>
      </div>

      {/* Service Selection Modal */}
      {showServiceModal && (
        <div className="modal-overlay" onClick={() => setShowServiceModal(false)}>
          <div className="service-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Services</h3>
              <button className="close-btn" onClick={() => setShowServiceModal(false)}>×</button>
            </div>
            
            <div className="modal-content">
              <div className="service-selection-form">
                <p className="form-description">
                  Select the services you want to offer and specify your experience. Prices are set from the service catalog.
                </p>

                <div className="services-grid">
                  {masterServices.map((masterService) => {
                    const selection = selectedServices.find(s => s.serviceId === masterService.id);
                    const isSelected = selection?.isSelected || false;
                    
                    return (
                      <div key={masterService.id} className={`service-card-compact ${isSelected ? 'selected' : ''}`}>
                        <div className="service-card-header">
                          <div className="service-icon">
                            {getSpiritualIcon(masterService.name)}
                          </div>
                          <div className="service-name">
                            <h4>{masterService.name}</h4>
                          </div>
                          <label className="service-checkbox">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => handleServiceSelection(masterService.id, e.target.checked)}
                            />
                          </label>
                        </div>

                        {isSelected && (
                          <div className="service-details-form">
                            <div className="form-row">
                              <div className="form-field">
                                <label>Experience (Years)</label>
                                <input
                                  type="number"
                                  min="0"
                                  max="50"
                                  value={selection?.experienceYears || ''}
                                  onChange={(e) => handleExperienceChange(masterService.id, parseInt(e.target.value) || 0)}
                                  placeholder="e.g. 5"
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {masterServices.length === 0 && (
                  <div className="loading-services">
                    <p>Loading available services...</p>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowServiceModal(false)}>
                Cancel
              </button>
              <button
                className="save-btn"
                onClick={handleSubmitServices}
                disabled={!selectedServices.some(s => s.isSelected)}
              >
                Add Selected Services ({selectedServices.filter(s => s.isSelected).length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProviderDashboard;